import React, {useCallback, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Image} from '../../../admin/artisticobjects/types/Image';
import {ArtisticObjectListing} from '../../../admin/artisticobjects/types/ArtisticObject';
import {getImageUrl} from '../../utils/imageUtils';
import {locationCodeMatches} from '../../utils/locationParser';
import ZoomableSvg from './ZoomableSvg';
import './DetailsPlanSection.scss';

interface DetailsPlanSectionProps {
  planImages: Image[];
  entityName?: string;
  title?: string;
  artisticObjects?: ArtisticObjectListing[];
  onLocationClick?: (locationCode: string, matchingObjects: ArtisticObjectListing[]) => void;
  highlightedLocationCode?: string;
  placeId?: string;
}

const SVG_MIME_TYPE = 'image/svg+xml';

const DetailsPlanSection: React.FC<DetailsPlanSectionProps> = ({
                                                                 planImages,
                                                                 entityName,
                                                                 title,
                                                                 artisticObjects = [],
                                                                 onLocationClick,
                                                                 highlightedLocationCode,
                                                                 placeId
                                                               }) => {
  const {t} = useTranslation();
  const displayTitle = title || t('details.sections.plan');
  const [isExpanded, setIsExpanded] = useState(false);

  const isSvgImage = (image: Image): boolean => {
    return image.mimeType === SVG_MIME_TYPE;
  };

  const findMatchingObjects = useCallback((locationCode: string): ArtisticObjectListing[] => {
    return artisticObjects.filter(obj => locationCodeMatches(locationCode, obj.locationCode));
  }, [artisticObjects]);

  const handleLocationClick = useCallback((locationCode: string) => {
    if (onLocationClick) {
      const matchingObjects = findMatchingObjects(locationCode);
      onLocationClick(locationCode, matchingObjects);
    }
  }, [findMatchingObjects, onLocationClick]);

  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  if (planImages.length === 0) {
    return null;
  }

  return (
    <div id="plan-card" className="bg-white shadow-sm p-6">
      <h3 className="text-lg font-semibold text-stone-900 mb-4">{displayTitle}</h3>
      <div className="space-y-3">
        {planImages.map((planImage, index) => (
          <div key={planImage.id} className="plan-image-container">
            {isSvgImage(planImage) ? (
              <div className="relative min-h-[300px]">
                <ZoomableSvg
                  svgUrl={getImageUrl(planImage.path)}
                  isExpanded={isExpanded}
                  onToggleExpand={handleToggleExpand}
                  onLocationClick={handleLocationClick}
                  highlightedLocationCode={highlightedLocationCode}
                  placeId={placeId}
                />
              </div>
            ) : (
              <img
                className="w-full max-w-md h-auto"
                src={getImageUrl(planImage.path)}
                alt={planImage.originalName || t('details.alt.plan', {entityName, index: index + 1})}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default DetailsPlanSection;
